import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { AudioProvider } from "@/components/AudioProvider";
import { getMe, waDeleted, waStatus } from "@/lib/api";
import Landing from "@/components/Landing";
import PinLogin from "@/components/PinLogin";
import Register from "@/components/Register";
import Dashboard from "@/components/Dashboard";
import Legal from "@/components/Legal";
import UsersAdmin from "@/components/UsersAdmin";
import StatsPanel from "@/components/StatsPanel";
import Meetings from "@/components/Meetings";
import WhatsApp from "@/components/WhatsApp";
import NexusAssistant from "@/components/NexusAssistant";
import NexusClipsy from "@/components/NexusClipsy";
import NexusTradingHub from "@/components/NexusTradingHub";
import { LiveTradingBadge } from "@/components/LiveTradingBadge";
import AVHelper from "@/components/AVHelper";
import NexusPonytail from "@/components/NexusPonytail";
import DevBridge from "@/components/DevBridge";
import EngineV2Tab from "@/components/EngineV2Tab";
import NexusMissAnne from "@/components/NexusMissAnne";
import HerculesFloor from "@/components/HerculesFloor";
import { MainNav, MoreSheet } from "@/components/MainNav";
import { NEXUS_MORE } from "@/components/NexusAssistant";
import { Toaster, toast } from "sonner";
import { ScanLine, Scale, ShieldCheck, TrendingUp, FileAudio, MessageSquareX, Bot, Wand2, Building2 } from "lucide-react";

const STORAGE_KEY = "yunusbol_session";

function MasterView({ session, onLogout }) {
  const [section, setSection] = useState("nexus");
  const [waUnread, setWaUnread] = useState(0);
  const [waOnline, setWaOnline] = useState(false);
  const seenRef = useRef(null); // Set of deleted msg_ids; null until baseline
  const sectionRef = useRef(section);
  sectionRef.current = section;

  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.08, ctx.currentTime);
      o.start(); o.stop(ctx.currentTime + 0.18);
      o.onended = () => ctx.close();
    } catch (e) { /* audio blocked */ }
  };

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const tick = async () => {
      try {
        const rows = await waDeleted(session.user_id);
        if (seenRef.current === null) {
          seenRef.current = new Set(rows.map((r) => r.msg_id));
          return; // baseline, no alerts for history
        }
        const fresh = rows.filter((r) => !seenRef.current.has(r.msg_id));
        if (fresh.length) {
          fresh.forEach((r) => seenRef.current.add(r.msg_id));
          const first = fresh[0];
          const preview = first.text ? first.text.slice(0, 80) : `${first.type} message`;
          const title = fresh.length === 1
            ? `Deleted message from ${first.sender_name || "someone"}`
            : `${fresh.length} messages just deleted`;
          toast.error(title, { description: preview });
          beep();
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try { new Notification("🚨 " + title, { body: preview, tag: "wa-deleted" }); } catch (e) {}
          }
          if (sectionRef.current !== "whatsapp") setWaUnread((n) => n + fresh.length);
        }
      } catch (e) { /* not connected yet */ }
    };
    const id = setInterval(tick, 3000);
    tick();
    return () => clearInterval(id);
  }, [session.user_id]);

  useEffect(() => {
    const tick = async () => {
      try { const s = await waStatus(session.user_id); setWaOnline(!!s.online); }
      catch (e) { setWaOnline(false); }
    };
    const id = setInterval(tick, 5000);
    tick();
    return () => clearInterval(id);
  }, [session.user_id]);

  const [nexusRole, setNexusRole] = useState("chat");
  const [toolTab, setToolTab] = useState("yunusbol");
  const [moreOpen, setMoreOpen] = useState(false);
  const LEGACY = { scanner: "yunusbol", legal: "legal", meet: "meet", access: "access", stats: "stats" };
  const openSection = (id) => {
    if (LEGACY[id]) { setToolTab(LEGACY[id]); id = "tools"; }
    setSection(id);
    if (id === "whatsapp") setWaUnread(0);
  };
  const toolTabs = [
    { id: "yunusbol", label: "Yunus Bol" }, { id: "legal", label: "A V Legal" }, { id: "meet", label: "Meeturai" },
    { id: "stats", label: "Statistics" }, { id: "access", label: "Access" },
  ];
  const moreItems = [
    ...NEXUS_MORE.map((m) => ({ ...m, key: m.role })),
    { key: "helper", label: "AV HELPER", icon: Wand2 }, { key: "trading", label: "A V TRADING", icon: Building2 },
    { key: "whatsapp", label: "WHATSAPP", icon: MessageSquareX, badge: waUnread },
  ];
  const pickMore = (it) => {
    setMoreOpen(false);
    if (it.role) { setNexusRole(it.role); openSection("nexus"); } else openSection(it.key);
  };
  const goSection = (id) => { if (id === "nexus") setNexusRole("chat"); openSection(id); };
  return (
    <div className="nx-shell">
      <MainNav section={section === "nexus" && nexusRole !== "chat" ? "more" : section} onSection={goSection} onMore={() => setMoreOpen(true)} moreBadge={waUnread}
        right={<div className="flex items-center gap-3"><span data-testid="wa-online-dot" title={waOnline ? "Laptop online" : "Laptop offline"} className={`w-2.5 h-2.5 rounded-full ${waOnline ? "bg-emerald-400 animate-pulse" : "bg-rose-500"}`} /><LiveTradingBadge userId={session.user_id} /></div>} />
      <main className="nx-main">
        {section === "nexus" ? (
          <NexusAssistant session={session} role={nexusRole} onRoleChange={setNexusRole} hideModes />
        ) : section === "ponytail" ? (
          <div className="h-full"><NexusPonytail session={session} /></div>
        ) : section === "devbridge" ? (
          <div className="h-full"><DevBridge /></div>
        ) : section === "enginev2" ? (
          <div className="h-full"><EngineV2Tab /></div>
        ) : section === "anne" ? (
          <div className="h-full overflow-y-auto"><NexusMissAnne /></div>
        ) : section === "hercules" ? (
          <HerculesFloor />
        ) : section === "clipsy" ? (
          <div className="h-full flex flex-col"><NexusClipsy /></div>
        ) : section === "helper" ? (
          <AVHelper session={session} />
        ) : section === "trading" ? (
          <div className="h-full"><NexusTradingHub session={session} /></div>
        ) : section === "whatsapp" ? (
          <WhatsApp session={session} />
        ) : (
          <div className="pb-6" data-testid="av-tools">
            <div className="nx-pills px-3 py-3 sticky top-0 z-20 bg-[var(--nx-bg)]/90 backdrop-blur" data-testid="av-tools-tabs">
              {toolTabs.map((t) => (
                <button key={t.id} onClick={() => setToolTab(t.id)} data-testid={`av-tools-tab-${t.id}`}
                  className={`nx-tap shrink-0 px-4 rounded-full nx-mono text-xs tracking-widest font-bold border ${toolTab === t.id ? "bg-[var(--nx-cyan)] text-[#05080f] border-transparent" : "border-cyan-300/20 text-cyan-100/70"}`}>{t.label.toUpperCase()}</button>
              ))}
              <button onClick={onLogout} data-testid="logout-button" className="nx-tap shrink-0 ml-auto px-4 rounded-full nx-mono text-xs border border-rose-400/40 text-rose-300">LOGOUT</button>
            </div>
            {toolTab === "yunusbol" ? <Dashboard session={session} onLogout={onLogout} />
              : toolTab === "legal" ? <Legal session={session} />
              : toolTab === "meet" ? <Meetings session={session} />
              : toolTab === "access" ? <UsersAdmin session={session} />
              : <StatsPanel session={session} />}
          </div>
        )}
      </main>
      {moreOpen && <MoreSheet items={moreItems} onPick={pickMore} onClose={() => setMoreOpen(false)} />}
    </div>
  );
}

function App() {
  const [session, setSession] = useState(null);
  const [view, setView] = useState("landing"); // landing | login | register

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    let s = null;
    try { s = JSON.parse(saved); } catch { s = null; }
    if (!s) return;
    setSession(s);
    getMe(s.user_id).then((fresh) => {
      if (fresh && fresh.ok) {
        setSession(fresh);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
      } else {
        setSession(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    }).catch(() => {});
  }, []);

  const handleLogin = (res) => {
    setSession(res);
    setView("landing");
    localStorage.setItem(STORAGE_KEY, JSON.stringify(res));
  };
  const handleLogout = () => {
    setSession(null);
    setView("landing");
    localStorage.removeItem(STORAGE_KEY);
  };

  if (session) {
    return (
      <AudioProvider>
        <Toaster position="top-center" richColors />
        {session.role === "master"
          ? <MasterView session={session} onLogout={handleLogout} />
          : <Dashboard session={session} onLogout={handleLogout} />}
      </AudioProvider>
    );
  }

  return (
    <AudioProvider>
      <Toaster position="top-center" richColors />
      {view === "login" ? (
        <PinLogin onLogin={handleLogin} onRegister={() => setView("register")} onBack={() => setView("landing")} />
      ) : view === "register" ? (
        <Register onRegistered={handleLogin} onBack={() => setView("landing")} />
      ) : (
        <Landing onEnter={() => setView("login")} onRegister={() => setView("register")} />
      )}
    </AudioProvider>
  );
}

export default App;
